import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { styled } from 'styled-components';

import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelS } from '@sorare/core/src/atoms/typography';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { range } from '@sorare/core/src/lib/arrays';

import type { TokenLoyaltyBoost_anyCard } from './__generated__/index.graphql';

type Props = {
  anyCard: TokenLoyaltyBoost_anyCard;
  selected?: boolean;
};

const StyledLabelL = styled(LabelL).attrs({
  bold: true,
  uppercase: true,
  brand25: true,
})``;
const StyledLabelS = styled(LabelS).attrs({
  bold: true,
  uppercase: true,
  brand25: true,
})``;

const getPlayerLoyaltyBoost = (
  anyCard: TokenLoyaltyBoost_anyCard,
  selected: boolean
) => {
  if (selected) {
    return anyCard.sealed
      ? anyCard.unsealedPlayerLoyaltyBoost
      : anyCard.sealedPlayerLoyaltyBoost;
  }
  return anyCard.sealed
    ? anyCard.sealedPlayerLoyaltyBoost
    : anyCard.unsealedPlayerLoyaltyBoost;
};

export const TokenLoyaltyBoost = ({ anyCard, selected = false }: Props) => {
  const maximumPoints =
    anyCard.unsealedPlayerLoyaltyBoost.maximumBoostLevel?.points ?? 1;

  const { playerLoyaltyBoost } = anyCard;

  const maybeSealedPlayerLoyaltyBoost = getPlayerLoyaltyBoost(
    anyCard,
    selected
  );

  return (
    <Vertical gap={0}>
      <SBHorizontal>
        <StyledLabelS uppercase color="var(--c-nd-600)">
          <FormattedMessage
            id="TokenLoyaltyBoost.loyalty"
            defaultMessage="Loyalty"
          />
        </StyledLabelS>
        <StyledLabelL color="var(--c-nd-900)">
          <FormattedNumber
            value={
              maybeSealedPlayerLoyaltyBoost.boostLevel.boostBasisPoints /
              100 /
              100
            }
            style="percent"
            signDisplay="always"
          />
        </StyledLabelL>
      </SBHorizontal>
      <Horizontal>
        <StyledLabelS color="var(--c-nd-900)">
          {maybeSealedPlayerLoyaltyBoost.boostLevel.points}/{maximumPoints}
        </StyledLabelS>
        <SplitProgressBar
          thresholds={range(maximumPoints).map(i => ({
            score: i + 1,
          }))}
          currentScore={playerLoyaltyBoost.points}
          previewScore={maybeSealedPlayerLoyaltyBoost.points}
          color="var(--c-loyalty)"
          variant="oblique"
        />
      </Horizontal>
    </Vertical>
  );
};

TokenLoyaltyBoost.fragments = {
  anyCard: gql`
    fragment TokenLoyaltyBoost_anyCard on AnyCardInterface {
      slug
      sealed
      sealedPlayerLoyaltyBoost {
        points
        boostLevel {
          points
          boostBasisPoints
        }
      }
      unsealedPlayerLoyaltyBoost {
        points
        boostLevel {
          points
          boostBasisPoints
        }
        maximumBoostLevel {
          points
        }
      }
      playerLoyaltyBoost {
        points
        boostLevel {
          points
        }
      }
    }
  ` as TypedDocumentNode<TokenLoyaltyBoost_anyCard>,
};
